class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems = this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === "cart-items") {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = "";

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace("[min]", event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace("[max]", event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace("[step]", event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute("name"),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          // Include .drawer__footer to refresh chibi/primer selections on cart changes
          const selectors = ["cart-drawer-items", ".drawer__footer", ".cart-drawer__footer"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          const sourceQty = html.querySelector("cart-items");
          if (sourceQty) this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items")?.dataset.id ?? "main-cart-items",
        selector: ".js-contents"
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section"
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section"
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer")?.dataset.id ?? "main-cart-footer",
        selector: ".js-contents"
      }
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    // Capture the perf marker BEFORE the loading spinner so the measurement
    // spans the real user-action-to-paint window (Dawn 15.4.1 fix).
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? "clear" : "change";
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(
      `${eventTarget}:user-action`
    );

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) ||
            document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll(".cart-item");

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute("value");
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle("is-empty", parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector("cart-drawer");
          const cartFooter = document.getElementById("main-cart-footer");

          if (cartFooter) cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
          if (cartDrawerWrapper)
            cartDrawerWrapper.classList.toggle("is-empty", parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1]
            ? parsedState.items[line - 1].quantity
            : undefined;
          let message = "";
          if (
            items.length === parsedState.items.length &&
            updatedValue !== parseInt(quantityElement.value)
          ) {
            if (typeof updatedValue === "undefined") {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace("[quantity]", updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) ||
            document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(
              cartDrawerWrapper.querySelector(".drawer__inner-empty"),
              cartDrawerWrapper.querySelector("a")
            );
          } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector(".cart-item__name"));
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId
        });
      })
      .catch(() => {
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(
          `${eventTarget}:user-action`,
          cartPerformanceUpdateMarker
        );
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector(".cart-item__error-text").textContent = message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") || document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") || document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add("hidden"));
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body }
            })
              .then(() => CartPerformance.measureFromEvent("note-update:user-action", event))
              .catch((error) => console.error("Cart note update failed:", error));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Free Chibi Promo, Primer Selection & Terms - Checkout Validation
document.addEventListener("DOMContentLoaded", () => {
  // ============================================
  // CART SELECTIONS: Primer Color & Chibi Choice
  // Note: Cart note is only updated on checkout, not on selection change
  // ============================================

  // Build the final cart note from selections + custom wishes
  const buildCartNote = (container) => {
    const parts = [];

    // Get primer selection (only if primer section exists)
    const primerSection = container?.querySelector(".primer-selection");
    if (primerSection) {
      const primerRadio = container.querySelector("input[data-primer-radio]:checked");
      if (primerRadio) {
        parts.push(`Primer Color: ${primerRadio.value}`);
      }
    }

    // Get chibi selection (only if chibi section exists)
    const chibiSection = container?.querySelector(".chibi-selection");
    if (chibiSection) {
      const chibiRadio = container.querySelector("input[data-chibi-radio]:checked");
      if (chibiRadio) {
        const sku = chibiRadio.dataset.sku;
        parts.push(
          sku ? `Free Chibi: ${chibiRadio.value} (SKU: ${sku})` : `Free Chibi: ${chibiRadio.value}`
        );
      }
    }

    // Get custom wishes from textarea
    const wishesField = container?.querySelector("textarea[data-wishes-note]");
    if (wishesField && wishesField.value.trim()) {
      parts.push(`Wishes: ${wishesField.value.trim()}`);
    }

    return parts.join(" | ");
  };

  // Save cart note to Shopify (returns a promise)
  const saveCartNote = async (note) => {
    const body = JSON.stringify({ note });
    try {
      await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        ...{ body }
      });
      return true;
    } catch (error) {
      console.error("Cart note update failed:", error);
      return false;
    }
  };

  // Clear errors when user makes selections
  document.addEventListener("change", (e) => {
    if (e.target.matches("[data-primer-radio]")) {
      const container = e.target.closest("[data-cart-selections]");
      if (container) {
        container.classList.remove("primer-error");
        const errorMsg = container.querySelector(".primer-error-message");
        if (errorMsg) errorMsg.setAttribute("hidden", "");
      }
    }
    if (e.target.matches("[data-chibi-radio]")) {
      const container = e.target.closest("[data-cart-selections]");
      if (container) {
        container.classList.remove("chibi-error");
        const errorMsg = container.querySelector(".chibi-selection .chibi-error-message");
        if (errorMsg) errorMsg.setAttribute("hidden", "");
      }
    }
  });

  // ============================================
  // VALIDATION
  // ============================================

  // Open and shake an accordion step to draw attention on validation error
  const openAndShakeStep = (stepEl) => {
    if (!stepEl) return;
    // Close all other steps, open this one
    const allSteps = stepEl.parentElement?.querySelectorAll(".cart-step") || [];
    allSteps.forEach((s) => {
      s.classList.remove("expanded");
      const h = s.querySelector(".cart-step__header");
      if (h) h.setAttribute("aria-expanded", "false");
    });
    stepEl.classList.add("expanded");
    const header = stepEl.querySelector(".cart-step__header");
    if (header) header.setAttribute("aria-expanded", "true");

    // Shake animation
    stepEl.classList.add("shake");
    setTimeout(() => stepEl.classList.remove("shake"), 500);

    // Scroll into view
    stepEl.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Validate primer selection (for Gold products)
  const validatePrimerSelection = (container) => {
    // Only validate if primer section is visible
    const primerSection = container?.querySelector(".primer-selection");
    if (!primerSection) return true;

    const primerRadio = container.querySelector("input[data-primer-radio]:checked");
    if (!primerRadio) {
      const selectionsContainer = container.querySelector("[data-cart-selections]");
      if (selectionsContainer) {
        selectionsContainer.classList.add("primer-error");
        const errorMsg = selectionsContainer.querySelector(".primer-error-message");
        if (errorMsg) errorMsg.removeAttribute("hidden");
      }
      // Open the primer accordion step
      const primerStep = primerSection.closest(".cart-step");
      openAndShakeStep(primerStep);
      return false;
    }
    return true;
  };

  // The shared cart-goals component owns visibility. This gate reads the same
  // canonical euro threshold and converts it once into presentment minor units.
  const isChibiRequired = (container) => {
    const chibiSection = container && container.querySelector(".chibi-selection");
    if (!chibiSection) return false;
    const thresholdEuros = Number.parseFloat(chibiSection.dataset.chibiThresholdEur || "");
    const cartTotal = Number.parseInt(chibiSection.dataset.cartTotal || "", 10);
    const currency = chibiSection.dataset.currency || "EUR";
    const rate =
      window.Shopify && window.Shopify.currency && window.Shopify.currency.rate
        ? Number.parseFloat(window.Shopify.currency.rate)
        : 1;
    if (!Number.isFinite(thresholdEuros) || !Number.isFinite(cartTotal)) return false;
    if (currency !== "EUR" && (!Number.isFinite(rate) || rate <= 0)) return false;

    const thresholdCents = Math.round(thresholdEuros * 100 * (currency === "EUR" ? 1 : rate));
    return cartTotal >= thresholdCents;
  };

  // Validate chibi selection (mandatory only when actually offered)
  const validateChibiSelection = (container) => {
    if (!isChibiRequired(container)) return true;

    const chibiRadio = container.querySelector("input[data-chibi-radio]:checked");
    if (chibiRadio) return true;

    // Show error
    const selectionsContainer = container.querySelector("[data-cart-selections]");
    if (selectionsContainer) {
      selectionsContainer.classList.add("chibi-error");
      const errorMsg = selectionsContainer.querySelector(".chibi-selection .chibi-error-message");
      if (errorMsg) errorMsg.removeAttribute("hidden");
    }
    // Open the chibi accordion step. If validation demands a chibi the step
    // must be visible — unhide defensively (non-EUR renders it hidden).
    const chibiSection = container.querySelector(".chibi-selection");
    const chibiStep = chibiSection ? chibiSection.closest(".cart-step") : null;
    if (chibiStep) chibiStep.hidden = false;
    openAndShakeStep(chibiStep);
    return false;
  };

  // Validate terms checkbox
  const validateTermsCheckbox = (container) => {
    const termsCheckbox = container?.querySelector("#cart-terms-checkbox");
    if (!termsCheckbox) return true; // No terms checkbox, proceed

    if (!termsCheckbox.checked) {
      const termsWrapper = termsCheckbox.closest(".cart__terms-checkbox");
      if (termsWrapper) {
        termsWrapper.classList.add("error", "shake");
        const errorMsg = termsWrapper.querySelector(".cart__terms-error");
        if (errorMsg) errorMsg.removeAttribute("hidden");
      }
      termsCheckbox.focus();

      setTimeout(() => {
        if (termsWrapper) termsWrapper.classList.remove("shake");
      }, 500);

      return false;
    }
    return true;
  };

  // Combined validation and cart note update for checkout
  const validateAndPrepareCheckout = async (container) => {
    let isValid = true;

    // Validate primer selection
    if (!validatePrimerSelection(container)) {
      isValid = false;
    }

    // Validate chibi selection
    if (!validateChibiSelection(container)) {
      isValid = false;
    }

    // Validate terms
    if (!validateTermsCheckbox(container)) {
      isValid = false;
    }

    // If valid, build and save the cart note
    if (isValid) {
      const note = buildCartNote(container);
      await saveCartNote(note);
    }

    return isValid;
  };

  // Cart drawer checkout button - use event delegation because the button
  // gets replaced when cart updates via AJAX
  document.addEventListener("click", async (e) => {
    const checkoutBtn = e.target.closest("#CartDrawer-Checkout");
    if (!checkoutBtn) return;

    // Skip if already validated
    if (checkoutBtn.dataset.validating === "true") return;
    if (checkoutBtn.dataset.validated === "true") {
      checkoutBtn.dataset.validated = "";
      return; // Allow the click to proceed
    }

    e.preventDefault();
    e.stopPropagation();
    checkoutBtn.dataset.validating = "true";

    try {
      const drawer = document.querySelector("cart-drawer");
      const isValid = await validateAndPrepareCheckout(drawer);
      if (isValid) {
        // Continue with checkout
        window.location.href = "/checkout";
      }
    } finally {
      checkoutBtn.dataset.validating = "";
    }
  });

  // Main cart page - use event delegation for checkout button clicks
  // This ensures validation works even after cart footer is re-rendered
  let cartCheckoutSubmitting = false;
  document.addEventListener("click", async (e) => {
    // Check if clicked element is the main cart checkout button
    const checkoutBtn = e.target.closest(
      '#main-cart-footer button[name="checkout"], #main-cart-footer #checkout'
    );
    if (!checkoutBtn) return;

    // Already submitting after validation passed — let the form submit proceed
    if (cartCheckoutSubmitting) return;
    if (checkoutBtn.dataset.validating === "true") return;

    e.preventDefault();
    e.stopPropagation();
    checkoutBtn.dataset.validating = "true";

    try {
      const cartFooter = document.querySelector("#main-cart-footer");
      const isValid = await validateAndPrepareCheckout(cartFooter);
      if (isValid) {
        // Navigate directly to /checkout — form.requestSubmit gets intercepted
        // by Shopify's AJAX cart handler (returns 204) instead of redirecting
        cartCheckoutSubmitting = true;
        window.location.href = "/checkout";
      }
    } finally {
      checkoutBtn.dataset.validating = "";
    }
  });

  // Clear terms error on change
  document.addEventListener("change", (e) => {
    if (e.target.matches("#cart-terms-checkbox")) {
      if (e.target.checked) {
        const termsWrapper = e.target.closest(".cart__terms-checkbox");
        if (termsWrapper) {
          termsWrapper.classList.remove("error");
          const errorMsg = termsWrapper.querySelector(".cart__terms-error");
          if (errorMsg) errorMsg.setAttribute("hidden", "");
        }
      }
    }
  });

  // ============================================
  // EXPRESS / DYNAMIC CHECKOUT GATE
  // The accelerated checkout buttons (Shop Pay, PayPal, …) submit straight to
  // Shopify and bypass the chibi/primer/terms validation that gates the main
  // checkout button. Lock them until the same gate passes, and keep the
  // selections note saved so express checkout still records the choices.
  // ============================================

  // Side-effect-free version of the validators (no shake/scroll) for the lock.
  const isGateSatisfied = (container) => {
    if (!container) return true;
    const primerSection = container.querySelector(".primer-selection");
    if (primerSection && !container.querySelector("input[data-primer-radio]:checked")) {
      return false;
    }
    if (isChibiRequired(container) && !container.querySelector("input[data-chibi-radio]:checked")) {
      return false;
    }
    const terms = container.querySelector("#cart-terms-checkbox");
    if (terms && !terms.checked) return false;
    return true;
  };

  let lastExpressNote = null;
  const updateExpressLock = (container) => {
    const root = container || document.querySelector("#main-cart-footer");
    if (!root) return;
    const express = root.querySelector(".cart__express-buttons");
    if (!express) return;
    const ok = isGateSatisfied(root);
    express.classList.toggle("is-locked", !ok);
    if (ok) {
      // Keep the cart note current so express checkout includes the selections.
      const note = buildCartNote(root);
      if (note !== lastExpressNote) {
        lastExpressNote = note;
        saveCartNote(note);
      }
    }
  };

  // Catch clicks on the locked express area (the CSS ::after overlay receives
  // them) and surface the same errors the main checkout button shows.
  document.addEventListener(
    "click",
    (e) => {
      const express = e.target.closest(".cart__express-buttons");
      if (!express || !express.classList.contains("is-locked")) return;
      e.preventDefault();
      e.stopPropagation();
      validateAndPrepareCheckout(document.querySelector("#main-cart-footer"));
    },
    true
  );

  // ============================================
  // NON-EUR CHIBI STEP REVEAL
  // For EUR carts Liquid decides server-side whether the chibi step renders.
  // For other currencies it renders `hidden` and JS must reveal it once the
  // converted threshold is met — isChibiRequired() already enforced that gate,
  // but nothing ever removed the `hidden` attribute, so non-EUR customers over
  // the threshold had checkout blocked by a step they could not see.
  // ============================================
  const updateChibiStepVisibility = (container) => {
    const root = container || document.querySelector("#main-cart-footer");
    const chibiSection = root && root.querySelector(".chibi-selection");
    if (!chibiSection) return;
    const currency = chibiSection.dataset.currency || "EUR";
    if (currency === "EUR") return; // server-side decision, leave untouched
    const step = chibiSection.closest(".cart-step");
    if (!step) return;
    const shouldShow = isChibiRequired(root);
    if (step.hidden === !shouldShow) return;
    step.hidden = !shouldShow;
    if (!shouldShow) {
      // Dropped back below the threshold: clear a stale selection so the cart
      // note can't carry a chibi the order no longer earns.
      const checked = step.querySelector("input[data-chibi-radio]:checked");
      if (checked) checked.checked = false;
    }
  };

  // Re-evaluate the lock on selection / terms changes…
  document.addEventListener("change", (e) => {
    if (e.target.matches("[data-primer-radio], [data-chibi-radio], #cart-terms-checkbox")) {
      updateExpressLock();
    }
  });

  // …and after Shopify re-renders the cart footer (which replaces the buttons
  // and re-stamps data-cart-total on quantity changes).
  const expressGateRoot = document.querySelector("#main-cart-footer");
  if (expressGateRoot) {
    let expressLockTimer = 0;
    new MutationObserver(() => {
      clearTimeout(expressLockTimer);
      expressLockTimer = setTimeout(() => {
        updateChibiStepVisibility();
        updateExpressLock();
      }, 80);
    }).observe(expressGateRoot, { childList: true, subtree: true });
  }

  updateChibiStepVisibility();
  updateExpressLock();
});
